import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Box, Divider, Typography } from "@mui/material";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

const number = (value) =>
  typeof value === "number"
    ? value.toLocaleString("en-US", { maximumFractionDigits: 1 })
    : "-";
const percentage = (value) =>
  typeof value === "number" ? `${number(value)}%` : "-";
const seconds = (value, suffix = "s") =>
  typeof value === "number" ? `${number(value)}${suffix}` : "-";

const statusColor = {
  Good: "#4CAF50",
  Warn: "#FF9800",
  Warning: "#FF9800",
  Moderate: "#FF9800",
  Critical: "#F44336",
};

const Metric = ({ label, value }) => {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography
        sx={{
          color: "#1C1C1E",
          fontSize: "13px",
          fontWeight: "bold",
          lineHeight: 1,
          mb: "8px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: statusColor[value] || "#4C6EF5",
          fontSize: "13px",
          fontWeight: "bold",
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const Detail = ({ title, metrics }) => (
  <Box sx={{ mt: "28px" }}>
    <Typography
      sx={{
        color: "#1A202C",
        fontSize: "16px",
        fontWeight: "bold",
        mb: "16px",
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: "repeat(5, minmax(0, 1fr))",
        },
        gap: "24px 32px",
      }}
    >
      {metrics.map((metric) => (
        <Metric key={metric.label} {...metric} />
      ))}
    </Box>
  </Box>
);

const SummaryBlock = ({ title, score, status, details }) => (
  <Box
    sx={{
      border: "1px solid #C7C7C7",
      borderRadius: "8px",
      bgcolor: "#fff",
      p: { xs: "16px", md: "16px 20px" },
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box>
        <Typography
          sx={{
            color: "#626262",
            fontSize: "13px",
            fontWeight: 600,
            mb: "14px",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "#626262",
            fontSize: "24px",
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {percentage(score)}
        </Typography>
        <Typography sx={{ color: "#8E8E93", fontSize: "12px", mt: "6px" }}>
          Last 7 Days
        </Typography>
      </Box>
      <Box
        sx={{
          minWidth: "86px",
          px: "12px",
          py: "4px",
          textAlign: "center",
          borderRadius: "3px",
          bgcolor: "#EEEEEE",
        }}
      >
        <Typography
          sx={{
            color: statusColor[status] || "#4A5568",
            fontSize: "13px",
            fontWeight: "bold",
            lineHeight: 1,
          }}
        >
          {status}
        </Typography>
      </Box>
    </Box>
    <Divider sx={{ borderColor: "#D5D5D5", my: "14px" }} />
    {details.map((detail) => (
      <Detail key={detail.title} {...detail} />
    ))}
  </Box>
);

const AInsight = ({ timeRange }) => {
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();
  const [requestUuid] = useState(uuidv4());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isAuthReady) return;
    const summaryReportId = timeRange?.summaryReportId;
    if (!summaryReportId) {
      setData(null);
      return;
    }
    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/aiInsight.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/aiInsight.read";
    const restfulPlaceholders = standalone
      ? { orgId: auth.realm?.id, siteId: auth.sites?.id, summaryReportId }
      : {
          entId: auth.realm?.id,
          orgId: auth.orgs?.id,
          siteId: auth.sites?.id,
          summaryReportId,
        };

    const load = async () => {
      const { getSendBatchData } = await sendBatch([
        { apiKey, requestUuid, restfulPlaceholders, params: {} },
      ]);
      setData(getSendBatchData({ requestUuid })?.data || null);
    };
    load();
  }, [auth, isAuthReady, requestUuid, sendBatch, standalone, timeRange?.summaryReportId]);

  if (!data) return null;

  const service = data.serviceExperience || {};
  const security = data.security || {};
  const roam = data.roam || {};
  const coverage = data.wirelessCoverage || {};
  const integrity = data.linkIntegrity || {};
  const operations = data.operations || {};
  const connect = service.connectSuccessRate || {};
  const connectTime = service.connectTime || {};
  const happiness = service.guestAccessHappiness || {};
  const access = happiness.durationDistribution || {};
  const rogue = security.rogueApDetection || {};
  const location = security.abnormalLocation || {};
  const roaming = roam.successRate || {};
  const signal = coverage.signalQualification || {};
  const wireless = integrity.wirelessCongestion || {};
  const wired = integrity.wiredCongestion || {};
  const degraded = integrity.wiredLinkDegration || {};
  const cable = integrity.wiredCableQuality || {};
  const channel = integrity.channelInterference || {};
  const reboot = operations.deviceReboot || {};
  const poe = operations.poeError || {};
  const errors = poe.errorCounts || {};
  const uplink = operations.uplinkInstability || {};
  const bottleneck = operations.systemBottleneck || {};
  const firmware = operations.firmwareStatus || {};
  const versions = firmware.distribution || {};

  const blocks = [
    {
      key: "serviceExperience",
      title: "Service Experience",
      score: service.score,
      status: service.status,
      details: [
        {
          title: "Captive Portal Connection Success Rate",
          metrics: [
            { label: "Status", value: connect.status || "-" },
            { label: "Success (Last 24 hrs)", value: number(connect.success) },
            { label: "Drop (Last 24 hrs)", value: number(connect.drop) },
            {
              label: "Attempts (Joined SSID)",
              value: number(connect.attempts),
            },
          ],
        },
        {
          title: "Captive Portal Authentication Time",
          metrics: [
            {
              label: "P95 Connect Time",
              value: seconds(connectTime.p95Seconds),
            },
            {
              label: "Threshold",
              value: seconds(connectTime.thresholdSeconds),
            },
            { label: "Median", value: seconds(connectTime.p50Seconds) },
            { label: "Sessions", value: number(connectTime.sessions) },
          ],
        },
        {
          title: "Captive Portal Access Satisfaction Score",
          metrics: [
            { label: "Sessions (Last 24h)", value: number(happiness.sessions) },
            { label: "< 1 Min", value: percentage(access["0-60s"]) },
            { label: "1-10 Mins", value: percentage(access["60-600s"]) },
            { label: "10-60 Mins", value: percentage(access["600-3600s"]) },
            { label: "> 60 Mins", value: percentage(access[">3600s"]) },
          ],
        },
      ],
    },
    {
      key: "security",
      title: "Security",
      score: security.score,
      status: security.status,
      details: [
        {
          title: "Rogue AP Detection",
          metrics: [
            {
              label: "Rogue APs Detected",
              value: number(rogue.rogueAp),
            },
            {
              label: "High-Risk Rogues",
              value: number(rogue.highRiskAp),
            },
            {
              label: "Impacted SSIDs",
              value: number(rogue.impactedSsid),
            },
          ],
        },
        {
          title: "Abnormal Location & Asset Displacement",
          metrics: [
            { label: "Secured", value: percentage(location.secured) },
            { label: "Learning", value: percentage(location.learning) },
            { label: "Displaced", value: percentage(location.displaced) },
          ],
        },
      ],
    },
    {
      key: "roam",
      title: "Roam",
      score: roam.score,
      status: roam.status,
      details: [
        {
          title: "Roaming Success Rate",
          metrics: [
            { label: "Status", value: roaming.status || "-" },
            { label: "Success Rate", value: percentage(roaming.successRate) },
            { label: "Total Events", value: number(roaming.totalEvents) },
            {
              label: "P95 Latency",
              value: seconds(roaming.p95LatencyMs, "ms"),
            },
          ],
        },
      ],
    },
    {
      key: "wirelessCoverage",
      title: "Wireless Coverage",
      score: coverage.score,
      status: coverage.status,
      details: [
        {
          title: "Wireless Signal Quality",
          metrics: [
            { label: "Status", value: signal.status || "-" },
            { label: "Total Clients", value: number(signal.totalClients) },
            { label: "Mean RSSI", value: seconds(signal.meanRssiDbm, " dBm") },
            {
              label: "Weak (<70dBm)",
              value: percentage(signal.weakSignalRate),
            },
          ],
        },
      ],
    },
    {
      key: "linkIntegrity",
      title: "Link Integrity",
      score: integrity.score,
      status: integrity.status,
      details: [
        {
          title: "Wireless Congestion Level",
          metrics: [
            { label: "Total APs", value: number(wireless.totalAps) },
            { label: "Status", value: wireless.status || "-" },
            { label: "Peak Congested", value: number(wireless.peakCongested) },
            {
              label: "Average Congested",
              value: number(wireless.avgCongested),
            },
            { label: "Threshold", value: number(wireless.threshold) },
          ],
        },
        {
          title: "Wired Congestion Level",
          metrics: [
            { label: "Status", value: wired.status || "-" },
            {
              label: "Peak Congested",
              value:
                typeof wired.peakCongested === "number"
                  ? `${number(wired.peakCongested)} Port(s)`
                  : "-",
            },
            {
              label: "Most Congested",
              value: wired.mostCongested?.deviceName || "-",
            },
            { label: "Threshold", value: number(wired.threshold) },
          ],
        },
        {
          title: "Wired Link Degradation",
          metrics: [
            {
              label: "Impacted Switch",
              value: number(degraded.affectedSwitchCount),
            },
          ],
        },
        {
          title: "Wired Cable Quality",
          metrics: [
            { label: "Status", value: cable.status || "-" },
            {
              label: "Noisy Port(s)",
              value:
                typeof cable.noisyPorts === "number"
                  ? `${number(cable.noisyPorts)} Port(s)`
                  : "-",
            },
            {
              label: "Impacted Switch",
              value: number(cable.switchesWithErrorPorts),
            },
          ],
        },
        {
          title: "Wireless Channel Interference & Neighboring AP Count",
          metrics: [
            { label: "Status", value: channel.status || "-" },
            { label: "Noisy SSIDs", value: number(channel.noisySsids) },
            { label: "Average Noise", value: seconds(channel.avgRssi, " dBm") },
          ],
        },
      ],
    },
    {
      key: "operations",
      title: "Operations",
      score: operations.score,
      status: operations.status,
      details: [
        {
          title: "Device Reboot Events",
          metrics: [
            { label: "Unexpected Reboot Status", value: reboot.status || "-" },
            { label: "Total Reboots", value: number(reboot.rebootCount) },
            {
              label: "Peak Reboot Count per Hour",
              value: number(reboot.peakRebootCount),
            },
          ],
        },
        {
          title: "PoE Fault Detection",
          metrics: [
            {
              label: "Used Power / Total Power Budget",
              value:
                typeof poe.usedPower === "number" &&
                typeof poe.totalPower === "number" &&
                poe.totalPower > 0
                  ? `${number(poe.usedPower)}W (${number((poe.usedPower / poe.totalPower) * 100)}%) / ${number(poe.totalPower)}W`
                  : "-",
            },
            { label: "Short Circuit", value: number(errors.pdShort) },
            { label: "Overload", value: number(errors.overload) },
            { label: "Power Denied", value: number(errors.powerDenied) },
            { label: "Other Errors", value: number(errors.others) },
          ],
        },
        {
          title: "Uplink Stability Status",
          metrics: [
            {
              label: "Device-to-platform Connectivity Status",
              value: uplink.status || "-",
            },
            {
              label: "Affected Device(s)",
              value: number(uplink.unlinkDevice),
            },
            { label: "Loss Event(s)", value: number(uplink.eventCount) },
          ],
        },
        {
          title: "System Performance Bottleneck",
          metrics: [
            {
              label: "Performance Compliance",
              value: bottleneck.status || "-",
            },
            { label: "Average Load", value: percentage(bottleneck.avg) },
            { label: "Threshold", value: percentage(bottleneck.threshold) },
          ],
        },
        {
          title: "Firmware Update Status",
          metrics: [
            {
              label: "Security Patch Compliance",
              value: firmware.status || "-",
            },
            { label: "Latest Version", value: percentage(versions.latest) },
            { label: "Previous Version", value: percentage(versions.previous) },
            { label: "Legacy", value: percentage(versions.legacy) },
          ],
        },
      ],
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        mx: "20px",
        mb: "20px",
      }}
    >
      {blocks.map((block) => (
        <SummaryBlock
          key={block.key}
          {...block}
        />
      ))}
    </Box>
  );
};

export default AInsight;
